import React, { useEffect, useState } from 'react';
import { RefreshCw, Download, Trash2, FileText, Clock } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Button, Badge, ConfirmDialog, LoadingState, EmptyState, Table, THead, TBody, TR, TH, TD } from '../../components/ui';

const SuperAdminExams = () => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    fetchExams();
  }, []);

  const fetchExams = async () => {
    try {
      setLoading(true);
      const res = await api.get('/super-admin/exams');
      setExams(res.data.data);
    } catch (err) {
      toast.error('Failed to load exams.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDownload = async () => {
    try {
      const res = await api.get('/results/export/bulk-all', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Bulk_Results_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Download started');
    } catch (err) {
      toast.error('Failed to download bulk results');
    } finally {
      setConfirmOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-950 min-h-screen">
        <LoadingState tone="dark" label="Loading exams…" />
      </div>
    );
  }

  return (
    <div className="bg-slate-950 min-h-screen p-4 sm:p-6 lg:p-8 text-slate-200">
      <div className="max-w-[90rem] mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold text-white tracking-tight">All Exams Overview</h1>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              Super Admin global view. Monitor all platform exams including inactive and deleted records.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="primary" tone="dark" icon={RefreshCw} onClick={fetchExams}>
              Refresh Data
            </Button>
            <Button variant="secondary" tone="dark" icon={Download} onClick={() => setConfirmOpen(true)}>
              Export All Data
            </Button>
          </div>
        </div>

        <div className="bg-slate-900 shadow-xl rounded-2xl border border-slate-800 overflow-hidden">
          <Table tone="dark" className="whitespace-nowrap">
            <THead>
              <TR>
                <TH>Exam Name</TH>
                <TH>Created By</TH>
                <TH>Timeline</TH>
                <TH className="text-center">Appeared</TH>
                <TH>Repetition</TH>
                <TH>Status</TH>
                <TH>Deleted By</TH>
              </TR>
            </THead>
            <TBody>
              {exams.length > 0 ? (
                exams.map((exam) => (
                  <TR key={exam._id} className={!exam.isActive ? 'bg-red-950/20' : ''}>
                    <TD>
                      <div className="flex flex-col">
                        <span className={`font-semibold ${!exam.isActive ? 'text-slate-400 line-through' : 'text-slate-100'}`}>
                          {exam.name}
                        </span>
                        <span className="text-[10px] text-slate-500 mt-0.5 font-mono">{exam._id}</span>
                      </div>
                    </TD>
                    <TD>
                      {exam.createdBy ? (
                        <div className="flex flex-col">
                          <span className="text-slate-300 font-medium">{exam.createdBy.name}</span>
                          <span className="text-xs text-slate-500">{exam.createdBy.email}</span>
                        </div>
                      ) : (
                        <span className="text-slate-500 italic text-xs">Unknown / System</span>
                      )}
                    </TD>
                    <TD>
                      <div className="flex flex-col gap-1 text-[11px]">
                        <div className="flex items-center gap-1.5 text-slate-300">
                          <Clock className="w-3.5 h-3.5 text-green-400" />
                          {new Date(exam.startTime).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-400">
                          <Clock className="w-3.5 h-3.5 text-red-400" />
                          {new Date(exam.endTime).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </TD>
                    <TD className="text-center">
                      <span className="inline-flex items-center justify-center min-w-[2.5rem] bg-slate-950 border border-slate-800 text-violet-400 text-xs font-bold font-mono px-2 py-1 rounded-md">
                        {exam.studentsAppeared}
                      </span>
                    </TD>
                    <TD>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${exam.repetition && exam.repetition !== 'NONE' ? 'bg-violet-950/40 text-violet-300 border-violet-800/50' : 'bg-slate-800 text-slate-500 border-slate-700'}`}>
                        {exam.repetition || 'NONE'}
                      </span>
                    </TD>
                    <TD>
                      {!exam.isActive ? (
                        <Badge variant="danger" tone="dark" className="uppercase tracking-wider">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                          DELETED
                        </Badge>
                      ) : (
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[10px] font-bold uppercase tracking-wider ${exam.status === 'ACTIVE' ? 'bg-emerald-950/30 text-emerald-400 border-emerald-900/50' :
                            exam.status === 'COMPLETED' ? 'bg-slate-800 text-slate-400 border-slate-700' :
                              'bg-amber-950/30 text-amber-500 border-amber-900/50'
                          }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${exam.status === 'ACTIVE' ? 'bg-emerald-400 animate-pulse' :
                              exam.status === 'COMPLETED' ? 'bg-slate-500' : 'bg-amber-500'
                            }`} />
                          {exam.status}
                        </span>
                      )}
                    </TD>
                    <TD>
                      {exam.deletedBy ? (
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-red-900/30 flex items-center justify-center text-red-400 shrink-0">
                            <Trash2 className="w-3.5 h-3.5" />
                          </div>
                          <span className="text-red-400 font-medium text-xs">{exam.deletedBy.name}</span>
                        </div>
                      ) : (
                        <span className="text-slate-600 text-sm">—</span>
                      )}
                    </TD>
                  </TR>
                ))
              ) : (
                <TR>
                  <TD colSpan="7">
                    <EmptyState tone="dark" icon={FileText} title="No exams found" description="No exams found in the system database." />
                  </TD>
                </TR>
              )}
            </TBody>
          </Table>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleBulkDownload}
        tone="dark"
        variant="danger"
        title="Export all results?"
        description="Downloading all student results for all exams may take a few moments and consume significant RAM. Do you want to proceed?"
        confirmLabel="Export"
      />
    </div>
  );
};

export default SuperAdminExams;
